import type { MsgKey } from "./i18n";
import {
  DEFAULT_GLOBALS, DEV_LIMIT, generatedCostGrid, actualC, fmt, levelTargetRolls, gridRows, gridSum, overlayGrid, pct, targetC,
  type Globals, type Level,
} from "./model.ts";
import { ratio } from "./big.ts";
import { gMilliToCoins } from "./g-milli.ts";

/** 校验文案一律返回 i18n key + 变量，翻译交给界面——这里不产生任何面向用户的成品字符串 */
export interface Msg { key: MsgKey; vars?: Record<string, string | number> }
export interface Check { key: string; label: Msg; ok: boolean; detail: Msg }

/**
 * 关卡校验。只判**系统配置列**（我方实际造价）——竞品是只读参照，
 * 用我方规则去判它会把 100% 的竞品数据标成「未通过」。
 */
export function validate(lv: Level, g: Globals = DEFAULT_GLOBALS) {
  const C = targetC(lv, g);
  // 手填是按格覆盖公式：没填的格按公式价补齐后再判完整 / 单调 / 对齐（与发布闸同口径）
  const sys = lv.costMatrixSeed != null ? generatedCostGrid(lv, g, true) : overlayGrid(lv, lv.sys, g);
  const grid = gridSum(sys, lv.N, lv.M);
  const rows = gridRows(sys, lv.N, lv.M);
  const total = grid.sum;
  // 偏差是比例，按 bigint 相减后再折成小数：两个 1e24 的数直接转 Number 相减，差值全是舍入噪声
  const dev = total == null || C == null || C === 0n ? null : ratio(total - C, C);
  const cells = lv.N * lv.M;

  // 栋内单调：同一栋楼升星只能越来越贵
  const badMono: Msg[] = [];
  rows.forEach((r, b) => {
    for (let l = 1; l < r.length; l++) {
      if (r[l] != null && r[l - 1] != null && r[l]! < r[l - 1]!) {
        badMono.push({ key: "v.mono.bad", vars: { b: b + 1, l: l + 1, prev: l } });
        break;
      }
    }
  });

  // 跨栋：后面的建筑不该比前面的便宜
  const firsts = rows.map((r) => r[0]);
  const badFirst = firsts.some((v) => v == null)
    ? []
    : (firsts.map((v, i) => (i && v! < firsts[i - 1]! ? i + 1 : null)).filter(Boolean) as number[]);

  const checks: Check[] = [
    {
      key: "i18n",
      label: { key: "v.i18n" },
      // **只有城市名必填**。主建筑名（stage）空着不算错——发布闸（config-validate.ts）
      // 从来不校验它，前端却把它算成失败，于是配置台红着、发布却放行，两道闸口径相反。
      // 影响面不是「几版草稿漏填」：全库几乎所有版本的 stage 都是空的，包括 a0=11
      // 当前生效的 v27。真按前端的口径，那些线上配置全是「不合格」的。
      // leo 2026-08-26 定：前端不为它报错。空着时照常显示城市名，不写「未配置」。
      ok: !!lv.name.trim(),
      detail: !lv.name.trim()
        ? { key: "v.i18n.noCity" }
        : { key: "v.i18n.ok", vars: { name: lv.name, stage: lv.stage.trim() || "—" } },
    },
    // 单掷金币版本按 g 讲，不提 B、E（leo 2026-10-22：页面上看不出口径换过）
    g.economyModel === 2 ? {
      key: "params",
      label: { key: "v.paramsG" },
      ok: C != null,
      detail: C == null
        ? { key: "v.params.missingG" }
        : { key: "v.params.okG", vars: { g: lv.gMilli ? gMilliToCoins(lv.gMilli) : "—", rolls: fmt(levelTargetRolls(lv, g)), c: fmt(C) } },
    } : {
      key: "params",
      label: { key: "v.params" },
      ok: C != null,
      detail: C == null
        ? { key: "v.params.missing" }
        : { key: "v.params.ok", vars: { b: fmt(lv.B), rolls: fmt(levelTargetRolls(lv, g)), c: fmt(C) } },
    },
    {
      key: "empty",
      label: { key: "v.empty", vars: { n: cells } },
      ok: grid.empty === 0,
      detail: grid.empty ? { key: g.economyModel === 2 ? "v.empty.someG" : "v.empty.some", vars: { n: grid.empty } } : { key: "v.empty.ok", vars: { n: cells } },
    },
    {
      key: "dev",
      label: { key: "v.dev", vars: { limit: pct(DEV_LIMIT, 0) } },
      ok: dev != null && Math.abs(dev) < DEV_LIMIT,
      detail: dev == null
        ? { key: "v.dev.na" }
        : {
            key: "v.dev.ok",
            vars: {
              total: fmt(total), c: fmt(C),
              src: actualC(lv) != null ? "cost.col.actual" : "cost.col.algo",
              dev: `${dev >= 0 ? "+" : ""}${pct(dev, 2)}`,
              verdict: Math.abs(dev) < DEV_LIMIT ? "v.dev.pass" : "v.dev.over",
            },
          },
    },
    {
      key: "mono",
      label: { key: "v.mono" },
      ok: lv.costMatrixSeed != null || badMono.length === 0,
      detail: lv.costMatrixSeed != null ? { key: "v.matrix.randomized" } : badMono.length ? badMono[0] : { key: "v.mono.ok", vars: { n: lv.N } },
    },
    {
      key: "first",
      label: { key: "v.first" },
      ok: lv.costMatrixSeed != null || badFirst.length === 0,
      detail: lv.costMatrixSeed != null ? { key: "v.matrix.randomized" } : badFirst.length
        ? { key: "v.first.bad", vars: { names: badFirst.map((n) => `#${n}`).join("、") } }
        : { key: "v.first.ok", vars: { values: firsts.map(fmt).join(" / ") } },
    },
  ];

  const activeChecks = lv.costMatrixSeed == null ? checks : checks.filter((c) => c.key !== "mono" && c.key !== "first");
  if (lv.costMatrixSeed != null) {
    const validSeed = Number.isInteger(lv.costMatrixSeed) && lv.costMatrixSeed >= -1 && lv.costMatrixSeed <= 2147483647;
    activeChecks.push({ key: "matrix", label: { key: "building.matrixSeed" }, ok: validSeed,
      detail: { key: validSeed ? "v.matrix.randomized" : "v.matrix.invalid" } });
  }
  return {
    checks: activeChecks,
    ok: activeChecks.every((c) => c.ok),
    C, total, dev, empty: grid.empty, rows,
    /** 列表页显示的短原因，同样是 key */
    // 直接用每条 check 自己的 detail，**不要再压成一句笼统的失败文案**。
    // detail 本来就算准了是哪个字段坏了（「主建筑（英文）为空」「有 3 格待填」
    // 「#2 比 #1 便宜」），压成「英文名未配置」之后：一是丢掉了定位信息，
    // 二是那句话看起来正好像在说城市名——填了 Tokyo 的人会以为校验有 bug
    //（leo 2026-08-26 实际踩到）。信息本来就有，只是没送到用户眼前。
    reasons: activeChecks.filter((c) => !c.ok).map((c) => c.detail),
  };
}

export type ValidateResult = ReturnType<typeof validate>;
